import React, { useState, useEffect, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import './billingOverview.scss';
import { I18n } from 'react-redux-i18n';
import viewBankinfoIcon from '../../../../assets/images/icons/viewBankinfo.svg';
import viewSetupIcon from '../../../../assets/images/icons/viewSetup.svg';
import viewTransactionIcon from '../../../../assets/images/icons/viewTransaction.svg';
import AutoRechargePopup from './component/AutoRechargePopup';
import TopupPopup from './component/TopupPopup';
import { callApiWithoutDispatch } from '../../../../utils/apiUtils';
import { formatCurrencyHasSymbol } from '../../../../utils/commonFunctions';
import {
  onBoardingBillingAutoRechargeSettingApi,
  onBoardingBillingTopupCreditApi,
  onBoardingGetFleetTransactionsApi,
} from '../../../../constants/ApiConfigs';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as loadingActions from '../../../../actions/loadingBarActions';
import CreditGrantsPage from './component/CreditGrants/CreditGrants';
import { TAB_LIST } from '../../BillingUsage';
import AddOnsReview from './component/AddOnsReview';
import TransactionHistory from './component/TransactionHistory';
import FleetPlan from './component/FleetPlan';
import GetStartedGuide from './component/GetStartedGuide';
import GetStartedSection from './component/GetStartedSection';
import WalletOverview from './component/WalletOverview';
import { handleResultBillingPayment } from '../../utils/billingCommonFunc';
import {
  FLEET_BILLING_MODE,
  REFRESH_BILLING_WARNING_AFTER_TOPUP_EVENT,
} from '../../utils/constants';

const getLast7DaysRange = () => {
  const today = new Date();
  const toDate = new Date(today);

  toDate.setUTCHours(23, 59, 59, 0);

  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 7);
  fromDate.setUTCHours(0, 0, 0, 0);

  return {
    toDate: toDate.toISOString(),
    fromDate: fromDate.toISOString(),
  };
};

const BillingOverview = (
  { billingUsage, isShowTopupPopup, setIsShowTopupPopup, ...props },
  context,
) => {
  const [isShowAutoRechargePopup, setIsShowAutoRechargePopup] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const gsnCreditBalance = useMemo(
    () => 
      billingUsage?.spendingLimit > 0 ? Math.max(0, Number(billingUsage?.gsnCreditBalance || 0) * -1) : billingUsage?.gsnCreditBalance,
    [billingUsage?.gsnCreditBalance, billingUsage?.spendingLimit],
  );
  useEffect(() => {
    handleGetTransactionHistory();
  }, []);

  const handleGetTransactionHistory = async () => {
    const { toDate, fromDate } = getLast7DaysRange();
    const transactionHistory = await callApiWithoutDispatch(
      onBoardingGetFleetTransactionsApi,
      {
        method: 'POST',
        body: JSON.stringify({
          criteria: {
            serviceType: 'all',
            transactionType: 'all',
            fleetId: props.auth.selectedFleet.fleetId,
            toDate: toDate,
            fromDate: fromDate,
          },
          from: 0,
          size: 5,
          fleetId: props.auth.selectedFleet.fleetId,
          _currentFleetId: props.auth.selectedFleet.fleetId,
        }),
      },
    );
    if (transactionHistory?.res?.list?.length > 0) {
      setTransactionHistory(transactionHistory?.res?.list);
    }
  };

  const formatCurrency = (amount) => {
    return formatCurrencyHasSymbol(amount, billingUsage?.currencyISO);
  };

  const renderListModal = () => {
    const handleTopupCredit = async (params = {}) => {
      try {
        props.loadingActions.showLoadingSpiner();
        const paramsBody = {
          fleetId: billingUsage?._id,
          amount: params?.amount,
          paymentId: params?.paymentMethodId,
          returnUrl: window.location.href,
          invoiceId: params?.invoiceId,
          makePayment: params?.makePayment,
        };
        if (params?.preview) {
          paramsBody.preview = params?.preview;
        }
        const response = await callApiWithoutDispatch(
          onBoardingBillingTopupCreditApi,
          {
            method: 'POST',
            body: JSON.stringify(paramsBody),
          },
        );
        props.loadingActions.hideLoadingSpiner();
        if (params?.preview) {
          return response?.res?.response;
        }
        const res = response?.res?.response || {};
        const { isSuccess, is3ds } = handleResultBillingPayment(res);
        if (isSuccess) {
          context.notification('success', I18n.t('billingUsage.topUpSuccess'));
          setIsShowTopupPopup(false);
          props.onLoadBillingUsage();
          handleGetTransactionHistory();
          window.dispatchEvent(
            new CustomEvent(REFRESH_BILLING_WARNING_AFTER_TOPUP_EVENT),
          );
        } else if (is3ds) {
          return;
        } else {
          context.notification(
            'error',
            I18n.t('billingUsage.topUpFailMessage'),
          );
        }
      } catch (error) {
        props.loadingActions.hideLoadingSpiner();
        context.notification('error', I18n.t('billingUsage.topUpFailMessage'));
      }
    };

    const handleBoardingBillingAutoRechargeSetting = async (params = {}) => {
      if (params?.autoCharge && params?.amount - params?.threshHold < 5) {
        context.notification(
          'error',
          'when auto charge is enabled, the amount must be greater than the recharge threshold (5)',
        );
        return;
      }
      try {
        props.loadingActions.showLoadingSpiner();
        const response = await callApiWithoutDispatch(
          onBoardingBillingAutoRechargeSettingApi,
          {
            method: 'POST',
            body: JSON.stringify({
              fleetId: billingUsage?._id,
              autoCharge: params?.autoCharge,
              amount: params?.amount,
              threshHold: params?.threshHold,
            }),
          },
        );
        props.loadingActions.hideLoadingSpiner();
        const res = response?.res?.response || {};
        const { isSuccess, is3ds } = handleResultBillingPayment(res);
        if (isSuccess) {
          context.notification(
            'success',
            I18n.t('billingUsage.autoRechargeSuccess'),
          );
          setIsShowAutoRechargePopup(false);
          props.onLoadBillingUsage();
        } else if (is3ds) {
          return;
        } else {
          context.notification(
            'error',
            I18n.t('billingUsage.autoRechargeFailMessage'),
          );
        }
      } catch (error) {
        props.loadingActions.hideLoadingSpiner();
        context.notification(
          'error',
          I18n.t('billingUsage.autoRechargeFailMessage'),
        );
      }
    };
    return (
      <>
        {isShowAutoRechargePopup && (
          <AutoRechargePopup
            onClose={() => setIsShowAutoRechargePopup(false)}
            billingUsage={billingUsage}
            onBoardingBillingAutoRechargeSetting={
              handleBoardingBillingAutoRechargeSetting
            }
          />
        )}
        {isShowTopupPopup && (
          <TopupPopup
            onClose={() => setIsShowTopupPopup(false)}
            billingUsage={billingUsage}
            handleTopup={handleTopupCredit}
            onOpenBillingUrl={props.onOpenBillingUrl}
            isMakePayment={
              billingUsage?.billingMode === FLEET_BILLING_MODE.invoice &&
              billingUsage?.spendingLimit > 0
            }
            gsnCreditBalance={gsnCreditBalance}
          />
        )}
      </>
    );
  };

  return (
    <div className="billing-overview-container">
      {renderListModal()}
      <Row>
        <Col sm={12} md={8}>
          <GetStartedSection />
          <WalletOverview
            billingUsage={billingUsage}
            formatCurrency={formatCurrency}
            setIsShowTopupPopup={setIsShowTopupPopup}
            setIsShowAutoRechargePopup={setIsShowAutoRechargePopup}
            viewTransactionIcon={viewTransactionIcon}
            viewBankinfoIcon={viewBankinfoIcon}
            viewSetupIcon={viewSetupIcon}
            permissions={props.permissions}
            onTabSelect={props.onTabSelect}
            notification={context.notification}
            loadingActions={props.loadingActions}
            onLoadBillingUsage={props.onLoadBillingUsage}
            onOpenBillingUrl={props.onOpenBillingUrl}
            gsnCreditBalance={gsnCreditBalance}
          />
          <CreditGrantsPage
            data={billingUsage?.creditGrants || []}
            onTabSelect={props.onTabSelect}
            billingUsage={billingUsage}
          />
          <TransactionHistory
            data={transactionHistory || []}
            onTabSelect={props.onTabSelect}
            billingUsage={billingUsage}
          />
          {billingUsage?.billingMode !== FLEET_BILLING_MODE.invoice && (
            <AddOnsReview
              billingUsage={billingUsage}
              onTabSelect={props.onTabSelect}
              auth={props.auth}
              loadingActions={props.loadingActions}
              notification={context.notification}
            />
          )}
        </Col>
        <Col sm={12} md={4}>
          <div className="get-started-sidebar">
            <GetStartedGuide />
            <FleetPlan
              billingUsage={billingUsage}
              onTabSelect={props.onTabSelect}
              loadingActions={props.loadingActions}
              notification={context.notification}
              auth={props.auth}
              context={context}
              handleLoadBillingUsage={props.onLoadBillingUsage}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

BillingOverview.contextTypes = {
  notification: PropTypes.func,
};

function mapDispatchToProps(dispatch) {
  return {
    loadingActions: bindActionCreators(loadingActions, dispatch),
  };
}
function mapStateToProps(state) {
  return {
    auth: state.auth,
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(BillingOverview);
